import React from 'react';
import { AlertTriangle, ArrowRight, Check } from 'lucide-react';
import { Button, Checkbox } from '../ui';
import { BomEditProposal, BomProposalEdit, BomProposalField } from '../../types/bomProposal';
import {
  formatProposalValue,
  lineLabel,
  ProposalAssessment,
  ProposalLineStatus,
  PROPOSAL_FIELD_LABELS,
} from '../../utils/bomProposal';
import { ProposalOutcome } from '../../hooks/useBomProposal';

/**
 * Robbie's proposed BOM changes, under the message that proposed them: his
 * summary, one row per line with each change as `field: before → after` and
 * his reason, a tick per line, and the buyer's Apply / Dismiss. Nothing is
 * written until Apply — the card is the visible diff that rule requires.
 *
 * Presentational: state comes from `useBomProposal` (the wizard wires it in
 * `pages/wizard/WizardBomProposal`), so the design gallery renders every
 * state from sample data. Apply is cobalt — applying is the buyer's action,
 * not Robbie's.
 */
export interface BomProposalCardProps {
  proposal: BomEditProposal;
  outcome: ProposalOutcome;
  assessment: ProposalAssessment;
  isTicked: (componentId: string) => boolean;
  /** Field changes across the ticked lines. */
  selectedChanges: number;
  applying: boolean;
  /** Robbie's account of a failed apply, already worded. */
  error: string | null;
  /** False for someone reading along: the diff shows, the controls do not. */
  canApply: boolean;
  onToggle: (componentId: string) => void;
  onApply: () => void;
  onDismiss: () => void;
}

const plural = (n: number, one: string) => `${n} ${one}${n === 1 ? '' : 's'}`;

/** Why a line cannot be ticked, in Robbie's voice; null when it can. */
const LINE_NOTE: Record<ProposalLineStatus, string | null> = {
  fresh: null,
  changed: 'This line changed since I suggested this, so I left it out.',
  missing: 'This line is no longer on the BOM.',
  done: 'Already on the BOM.',
};

const ChangeLine: React.FC<{ edit: BomProposalEdit; field: BomProposalField }> = ({ edit, field }) => (
  <li className="text-meta leading-[1.45] text-ink-body">
    <span className="text-ink-muted">{PROPOSAL_FIELD_LABELS[field]}: </span>
    <span className="break-words text-ink-secondary line-through decoration-line-strong">
      {formatProposalValue(field, edit.before[field])}
    </span>
    <ArrowRight aria-label="becomes" className="mx-[3px] inline h-[11px] w-[11px] align-[-1px] text-ink-muted" />
    <span className="break-words font-medium text-ink">{formatProposalValue(field, edit.changes[field])}</span>
  </li>
);

const ProposalRow: React.FC<{
  edit: BomProposalEdit;
  status: ProposalLineStatus;
  ticked: boolean;
  selectable: boolean;
  onToggle: () => void;
}> = ({ edit, status, ticked, selectable, onToggle }) => {
  const heading = (
    <span className="flex min-w-0 flex-col">
      <span className="break-words text-body-sm font-semibold text-ink">{lineLabel(edit)}</span>
      {edit.name && edit.partNumber && <span className="font-mono text-[10.5px] text-ink-muted">{edit.partNumber}</span>}
    </span>
  );
  const note = LINE_NOTE[status];
  return (
    <li className="flex flex-col gap-[3px] border-t border-line-divider py-s3 first:border-t-0">
      {selectable ? (
        <Checkbox
          checked={ticked}
          disabled={status !== 'fresh'}
          onCheckedChange={onToggle}
          label={heading}
          labelClassName="min-w-0"
          className="items-start [&>span:first-of-type]:mt-[2px]"
        />
      ) : (
        heading
      )}
      <ul className={`m-0 flex list-none flex-col gap-[2px] p-0 ${selectable ? 'pl-[23px]' : ''}`}>
        {(Object.keys(edit.changes) as BomProposalField[]).map((field) => (
          <ChangeLine key={field} edit={edit} field={field} />
        ))}
      </ul>
      <p className={`m-0 text-meta leading-[1.45] text-ink-secondary ${selectable ? 'pl-[23px]' : ''}`}>{edit.reason}</p>
      {note && <p className={`m-0 text-meta text-warn-ink ${selectable ? 'pl-[23px]' : ''}`}>{note}</p>}
    </li>
  );
};

/** The one line a settled card collapses to. */
const Settled: React.FC<{ outcome: Exclude<ProposalOutcome, { status: 'open' }> }> = ({ outcome }) => {
  if (outcome.status === 'dismissed') {
    return <p className="m-0 mt-s3 text-meta text-ink-muted">Dismissed</p>;
  }
  if (outcome.status === 'unchanged') {
    return (
      <p className="m-0 mt-s3 text-meta text-ink-secondary">
        Nothing to apply — the BOM already holds these values, so I didn't record a new version.
      </p>
    );
  }
  return (
    <p role="status" className="m-0 mt-s3 flex items-center gap-s2 text-meta font-medium text-success-ink">
      <Check className="h-[13px] w-[13px] shrink-0" />
      Applied {plural(outcome.count, 'change')}
      {outcome.version !== null && <span className="font-mono">{` · v${outcome.version}`}</span>}
    </p>
  );
};

export const BomProposalCard: React.FC<BomProposalCardProps> = ({
  proposal,
  outcome,
  assessment,
  isTicked,
  selectedChanges,
  applying,
  error,
  canApply,
  onToggle,
  onApply,
  onDismiss,
}) => {
  if (outcome.status !== 'open') return <Settled outcome={outcome} />;

  const selectable = canApply && !assessment.staleBom;
  return (
    <section
      aria-label="Proposed BOM changes"
      className="mt-s3 flex flex-col rounded-[9px] border border-robbie-border bg-surface px-[10px] py-s3"
    >
      <span className="text-[9.5px] font-bold uppercase tracking-[0.1em] text-robbie-ink">
        Proposed changes · {plural(proposal.edits.length, 'line')}
      </span>
      {proposal.summary && <p className="m-0 mt-[3px] text-body-sm text-ink-body">{proposal.summary}</p>}

      <ul className="m-0 mt-s2 flex list-none flex-col p-0">
        {proposal.edits.map((edit) => (
          <ProposalRow
            key={edit.componentId}
            edit={edit}
            status={assessment.staleBom ? 'fresh' : assessment.lines[edit.componentId] ?? 'fresh'}
            ticked={isTicked(edit.componentId)}
            selectable={selectable}
            onToggle={() => onToggle(edit.componentId)}
          />
        ))}
      </ul>

      {assessment.staleBom && (
        <p className="m-0 mt-s2 text-meta text-warn-ink">
          I wrote this for an earlier extraction of the BOM, so I can't apply it. Ask me again and I'll look at the current lines.
        </p>
      )}
      {!canApply && (
        <p className="m-0 mt-s2 text-meta text-ink-muted">Only an editor of this RFQ can apply these.</p>
      )}
      {error && (
        <p role="alert" className="m-0 mt-s2 flex items-start gap-s2 text-meta text-danger-ink">
          <AlertTriangle className="mt-[1px] h-[12px] w-[12px] shrink-0" />
          {error}
        </p>
      )}

      {canApply && (
        <div className="mt-s3 flex flex-wrap items-center gap-s2 border-t border-line-divider pt-s3">
          {!assessment.staleBom && (
            <Button size="sm" onClick={onApply} disabled={selectedChanges === 0 || applying}>
              {applying ? 'Applying…' : `Apply ${plural(selectedChanges, 'change')}`}
            </Button>
          )}
          <Button size="sm" variant="ghost" onClick={onDismiss} disabled={applying}>
            Dismiss
          </Button>
        </div>
      )}
    </section>
  );
};

export default BomProposalCard;
